import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePendingApprovalsBell } from '../../hooks/usePendingApprovalsBell';

export default function NotificationBell() {
  const navigate = useNavigate();
  const { canSeeBell, total, breakdown } = usePendingApprovalsBell();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [isOpen]);

  if (!canSeeBell) return null;

  const rows = [
    { label: 'GRNs pending', value: breakdown?.grnCount ?? 0 },
    { label: 'Dispatches pending', value: breakdown?.dispatchCount ?? 0 },
    { label: 'Past GRN edits pending', value: breakdown?.pastGrnCount ?? 0 },
    { label: 'Past Dispatch edits pending', value: breakdown?.pastDispatchCount ?? 0 },
  ].filter((row) => row.value > 0);

  const goToApprovals = () => {
    setIsOpen(false);
    navigate('/inventory/pending');
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="relative w-9 h-9 rounded-md bg-white/8 border border-white/10 hover:bg-white/15 text-white flex items-center justify-center transition-colors"
        aria-label="Pending approvals"
      >
        <i className="fas fa-bell text-sm"></i>
        {total > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-soft">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white border border-ink-200 rounded-lg shadow-panel z-50 text-ink-900 animate-fade-in overflow-hidden">
          <div className="px-4 py-3 border-b border-ink-100 bg-ink-50/80">
            <p className="text-sm font-bold">Pending Approvals</p>
            <p className="text-xs text-ink-500 mt-0.5">
              {total > 0 ? `${total} item${total === 1 ? '' : 's'} awaiting your review` : 'Nothing pending right now'}
            </p>
          </div>

          {rows.length > 0 && (
            <div className="py-1">
              {rows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between px-4 py-2 text-sm"
                >
                  <span className="text-ink-600">{row.label}</span>
                  <span className="font-bold text-ink-900">{row.value}</span>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={goToApprovals}
            className="w-full px-4 py-2.5 text-sm font-semibold text-brand-600 hover:bg-brand-50 border-t border-ink-100 transition-colors"
          >
            View Pending Approvals
          </button>
        </div>
      )}
    </div>
  );
}
